import React from "react";
import styled from "styled-components";
import WifiTetheringOutlinedIcon from "@mui/icons-material/WifiTetheringOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { colors } from "../../../Shared/colors";
import { formatDuration } from "./socialLeadsFormat";

/**
 * Whether Meta is still delivering leads, stated plainly.
 *
 * The "Synced" timestamp above says when this page last fetched — which stayed
 * healthy-looking throughout the sixteen days the webhook was switched off.
 * This reports the connection instead: leads old enough that a working webhook
 * would have posted them, still missing from the CRM.
 *
 * Silent when everything is fine and there is nothing to say, so that its
 * appearance is itself the signal.
 */
export const SocialLeadsHealth = ({ health }) => {
  if (!health) return null;

  const { delivering, undelivered, automatic, manual, minutesSinceLastAutomatic } =
    health;

  if (!delivering) {
    return (
      <Banner $tone="bad">
        <WarningAmberOutlinedIcon />
        <div>
          <p className="title">
            {undelivered} {undelivered === 1 ? "lead" : "leads"} never reached the
            CRM
          </p>
          <p className="detail">
            {undelivered === 1 ? "It was" : "They were"} submitted on Meta over{" "}
            {formatDuration(health.graceMinutes)} ago and still {undelivered === 1 ? "hasn't" : "haven't"}{" "}
            arrived. Import {undelivered === 1 ? "it" : "them"} below to recover{" "}
            {undelivered === 1 ? "it" : "them"} now, then check that the{" "}
            <strong>leadgen</strong> webhook field is still subscribed in the Meta
            app dashboard.
          </p>
        </div>
      </Banner>
    );
  }

  // Delivering, but everything arrived by hand — the webhook is not doing its
  // job even though nothing is currently missing.
  if (automatic === 0 && manual > 0) {
    return (
      <Banner $tone="warn">
        <WarningAmberOutlinedIcon />
        <div>
          <p className="title">Every lead here was imported by hand</p>
          <p className="detail">
            Nothing in this range arrived automatically. The webhook may be
            switched off — new leads will keep needing manual import until it is
            fixed.
          </p>
        </div>
      </Banner>
    );
  }

  if (automatic === 0) return null;

  return (
    <Banner $tone="good">
      <WifiTetheringOutlinedIcon />
      <div>
        <p className="title">
          Meta is delivering
          {minutesSinceLastAutomatic !== null ? (
            <> — last lead arrived {formatDuration(minutesSinceLastAutomatic)} ago</>
          ) : null}
        </p>
        <p className="detail">
          <strong>{automatic}</strong> captured automatically
          {manual > 0 ? (
            <>
              , <strong>{manual}</strong> imported by hand
            </>
          ) : null}
          .
        </p>
      </div>
    </Banner>
  );
};

const toneColor = (tone) =>
  tone === "bad" ? colors.veryHot : tone === "warn" ? colors.moderate : colors.success;

const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 11px;
  background: ${colors.white};
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-left: 3px solid ${(props) => toneColor(props.$tone)};
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1);

  > svg {
    font-size: 1.35rem;
    color: ${(props) => toneColor(props.$tone)};
    flex-shrink: 0;
  }

  .title {
    margin: 0;
    font-size: 0.85rem;
    font-weight: 700;
    color: ${(props) =>
      props.$tone === "good" ? colors.text : toneColor(props.$tone)};
  }

  .detail {
    margin: 3px 0 0 0;
    font-size: 0.78rem;
    line-height: 1.5;
    color: ${colors.textMed};
  }
  .detail strong {
    color: ${colors.text};
    font-variant-numeric: tabular-nums;
  }
`;
